
import { useCallback, useReducer } from "react";
import { ViewOptionStateEnum } from "../../components/contextual-app-bar/ViewOptionStateEnum";
import { CustomDurationDialogValues } from "../../components/custom-duration-dialog/CustomDurationDialog";
import CustomDurationUtils, { DurationOptionEnum } from "../../components/custom-duration-dialog/CustomDurationUtils";
import { addHours } from "date-fns";
import _ from "lodash";

export type AlarmDashboardStateType = {
    viewOptionState: ViewOptionStateEnum,
    groupID: number | undefined,
    durationLabel: string,
    durationOptions: CustomDurationDialogValues;
    showVoice: boolean;
    showText: boolean;
    showEmail: boolean;
};

export let defaultAlarmDashboardState: AlarmDashboardStateType = {
    viewOptionState: ViewOptionStateEnum.Default,
    groupID: undefined,
    durationLabel: CustomDurationUtils.getCustomDurationLabel(DurationOptionEnum.OneDay, true),
    durationOptions: {
        option: DurationOptionEnum.OneDay,
        duration: {
            startDate: addHours(new Date(), -24),
            endDate: new Date()
        }
    },
    showEmail: true,
    showText: true,
    showVoice: true

};

enum AlarmDashboardStateActionType {

    GroupChanged = 'ALARM_DASHBOARD_GROUP_CHANGED',
    DurationChanged = 'ALARM_DASHBOARD_DURATION_CHANGED',
    ShowEmailChanged = 'ALARM_DASHBOARD_EMAIL_CHANGED',
    ShowTextChanged = 'ALARM_DASHBOARD_TEXT_CHANGED',
    ShowVoiceChanged = 'ALARM_DASHBOARD_VOICE_CHANGED',
    ResetChanged = 'ALARM_DASHBOARD_RESET_CHANGED'
}

type AlarmDashboardStateActions = {
    type: AlarmDashboardStateActionType.GroupChanged;
    payload: number;
} | {
    type: AlarmDashboardStateActionType.DurationChanged;
    payload: CustomDurationDialogValues;
} | {
    type: AlarmDashboardStateActionType.ShowEmailChanged;
    payload: boolean;
} | {
    type: AlarmDashboardStateActionType.ShowTextChanged;
    payload: boolean;
} | {
    type: AlarmDashboardStateActionType.ShowVoiceChanged;
    payload: boolean;
} | {
    type: AlarmDashboardStateActionType.ResetChanged;
    payload: void;
};


const optionsChanged = (newState: AlarmDashboardStateType, 
    defaultState: AlarmDashboardStateType) => 
    newState.durationOptions.option !== defaultState.durationOptions.option ||
    newState.showText !== defaultState.showText || 
    newState.showEmail !== defaultState.showEmail || 
    newState.showVoice !== defaultState.showVoice;

function AlarmDashboardStateReducer(state: AlarmDashboardStateType, action: AlarmDashboardStateActions): AlarmDashboardStateType {

    const copy = _.cloneDeep(state);

    switch (action.type) {
        
        case AlarmDashboardStateActionType.GroupChanged:
            copy.groupID = action.payload;
            return copy;


        case AlarmDashboardStateActionType.DurationChanged:

            copy.durationLabel = CustomDurationUtils.getCustomDurationLabel(action.payload.option, true);
            copy.durationOptions =  {
                option: action.payload.option,
                duration: {
                    startDate: new Date(action.payload.duration.startDate as Date),
                    endDate: new Date(action.payload.duration.endDate as Date)
                }
            };

            copy.viewOptionState = optionsChanged(copy, defaultAlarmDashboardState) ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;

            return copy;


        case AlarmDashboardStateActionType.ShowEmailChanged:
            copy.showEmail = action.payload;
            copy.viewOptionState = optionsChanged(copy, defaultAlarmDashboardState) ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            return copy;            

        case AlarmDashboardStateActionType.ShowTextChanged:
            copy.showText = action.payload;
            copy.viewOptionState = optionsChanged(copy, defaultAlarmDashboardState) ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            return copy;

        case AlarmDashboardStateActionType.ShowVoiceChanged:
            copy.showVoice = action.payload;
            copy.viewOptionState = optionsChanged(copy, defaultAlarmDashboardState) ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            return copy;            
    
                
        case AlarmDashboardStateActionType.ResetChanged:
            return {...defaultAlarmDashboardState};

        default:
            return state;
    }
}

export default function useAlarmDashboardState() {

    const [state, dispatch] = useReducer(AlarmDashboardStateReducer, {...defaultAlarmDashboardState});

    const setGroup = useCallback((value: number) => dispatch({
        type: AlarmDashboardStateActionType.GroupChanged,
        payload: value
    }), []);

    const setDuration = useCallback((value: CustomDurationDialogValues) => dispatch({
        type: AlarmDashboardStateActionType.DurationChanged,
        payload: value
    }), []);


    const setShowEmail = useCallback((value: boolean) => dispatch({
        type: AlarmDashboardStateActionType.ShowEmailChanged,
        payload: value
    }), []);

    const setShowText = useCallback((value: boolean) => dispatch({
        type: AlarmDashboardStateActionType.ShowTextChanged,
        payload: value
    }), []);

    const setShowVoice = useCallback((value: boolean) => dispatch({
        type: AlarmDashboardStateActionType.ShowVoiceChanged,
        payload: value
    }), []);

    const resetState = useCallback(() => dispatch({
        type: AlarmDashboardStateActionType.ResetChanged,
        payload: undefined
    }), []);



    return {
        state,
        setGroup,
        setDuration,
        setShowEmail,
        setShowText,
        setShowVoice,
        resetState
    };
}

